import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import {
  Accordion,
  AccordionDetails,
  Box,
  Grid,
  CircularProgress,
  Backdrop,
  Typography,
  Divider,
  Paper,
  Collapse,
  IconButton,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { moneyformatter } from "src/common/utils/helpers";
import Heading from "./Heading";
import { useStyles } from "./styles";

type Props = {
  expand: boolean;
  updateExpandStatus: (type: number) => void;
};

function dispatchRowKey(detail: Record<string, unknown>, index: number): string {
  const id =
    detail?.orderCrewDetailId ??
    detail?.orderCrewDetailID ??
    detail?.OrderCrewDetailId ??
    detail?.OrderCrewDetailID;
  return id != null && id !== "" ? String(id) : `idx-${index}`;
}

function readDeductionItems(detail: Record<string, unknown>): Record<string, unknown>[] {
  const candidates = [
    detail?.incompletePunchListItems,
    detail?.IncompletePunchListItems,
    detail?.deductionItems,
    detail?.DeductionItems,
    detail?.deductionList,
    detail?.DeductionList,
    detail?.crewDeductions,
    detail?.CrewDeductions,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c as Record<string, unknown>[];
    if (typeof c === "string" && c.trim()) {
      try {
        const parsed = JSON.parse(c);
        if (Array.isArray(parsed)) return parsed as Record<string, unknown>[];
      } catch {
        // Ignore malformed JSON and try other candidate keys.
      }
    }
  }
  return [];
}

function deductionAmount(item: Record<string, unknown>): string | number | undefined {
  const v =
    item?.amount ??
    item?.Amount ??
    item?.deductionAmount ??
    item?.DeductionAmount ??
    item?.nteDeduction ??
    item?.NteDeduction;
  return v as string | number | undefined;
}

/** Prefer Exception when set; otherwise Description (Jan-IT crew dispatch behavior). */
function deductionReason(item: Record<string, unknown>): string {
  const exception = item?.exception ?? item?.Exception;
  if (typeof exception === "string" && exception.trim()) return exception.trim();
  const description = item?.description ?? item?.Description;
  if (typeof description === "string" && description.trim()) return description.trim();
  return "";
}

export function Crew({ expand, updateExpandStatus }: Props): React.ReactElement {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [loader, setLoader] = useState(false);
  const [crewDetails, setCrewDetails] = useState<any[]>([]);
  const [crewHeader, setCrewHeader] = useState<{ nte?: number; deductions?: number; total?: number } | null>(null);
  const [expandedDispatchKeys, setExpandedDispatchKeys] = useState<Set<string>>(new Set());
  const { crewId } = useParams<{ crewId: any }>();

  const loadCrewData = useCallback(async () => {
    if (!crewId) return;
    try {
      setLoader(true);
      const [crewHeaderRes, crewDetailsRes] = await Promise.all([
        fetchWrapper.get(`get-order-crew-by-crew-id/${crewId}`),
        fetchWrapper.get(`get-order-crew-detail-by-crew-id/${crewId}`),
      ]);
      setCrewHeader({
        nte: crewHeaderRes?.nte,
        deductions: crewHeaderRes?.deductions,
        total: crewHeaderRes?.total,
      });
      const details = crewDetailsRes?.crewDetails ?? [];
      setCrewDetails(details);
      setExpandedDispatchKeys(new Set());
    } catch (error: any) {
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader(false);
    }
  }, [crewId, dispatch]);

  React.useEffect(() => {
    if (expand) {
      loadCrewData();
    }
  }, [expand, loadCrewData]);

  const allDispatchesExpanded =
    crewDetails.length > 0 &&
    crewDetails.every((d, i) => expandedDispatchKeys.has(dispatchRowKey(d, i)));

  const toggleDispatchRow = (key: string) => {
    setExpandedDispatchKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleExpandAllDispatches = () => {
    if (allDispatchesExpanded) {
      setExpandedDispatchKeys(new Set());
    } else {
      setExpandedDispatchKeys(new Set(crewDetails.map((d, i) => dispatchRowKey(d, i))));
    }
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={loader}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box border={1} mb={2} borderColor="grey.200">
        <Accordion square elevation={0} expanded={expand} onChange={() => updateExpandStatus(3)}>
          <Heading heading="Crew" />
          <Box borderTop={1} mb={1} borderColor="grey.200"></Box>
          <AccordionDetails>
            <Paper elevation={0} square className={classes.root}>
              <Box p={1}>
                {crewHeader != null && (
                  <Box mb={2} pl={1} pr={1} py={1} width="100%">
                    <Box
                      className={classes.crewHeaderRow}
                      display="flex"
                      flexDirection="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      width="100%"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Box className={classes.crewSummaryWrap}>
                        <Box display="flex" flexDirection="row" alignItems="baseline" width="100%">
                          <Box className={classes.crewLabelCol}>
                            <Typography variant="body1" className={classes.labelText}>
                              Original NTE:
                            </Typography>
                          </Box>
                          <Box className={classes.crewValueCol}>
                            <Typography variant="body1" className={classes.valueText}>
                              {moneyformatter(crewHeader.nte)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" flexDirection="row" alignItems="baseline" width="100%" mt={0.5}>
                          <Box className={classes.crewLabelCol}>
                            <Typography variant="body1" className={classes.labelText}>
                              Deductions:
                            </Typography>
                          </Box>
                          <Box className={classes.crewValueCol}>
                            <Typography variant="body1" className={classes.valueText}>
                              {moneyformatter(crewHeader.deductions)}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" flexDirection="row" alignItems="baseline" width="100%" mt={0.5}>
                          <Box className={classes.crewLabelCol}>
                            <Typography variant="body1" className={classes.labelText}>
                              Total NTE:
                            </Typography>
                          </Box>
                          <Box className={classes.crewValueCol}>
                            <Typography variant="body1" className={classes.valueText}>
                              {moneyformatter(crewHeader.total)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      {crewDetails.length > 0 && (
                        <Box className={classes.expandAllWrap}>
                          <Button variant="outlined" onClick={toggleExpandAllDispatches} className={classes.expandAllButton}>
                            {allDispatchesExpanded ? "Collapse All" : "Expand All"}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                {crewDetails.map((crewDetail: any, index: number) => {
                  const rowKey = dispatchRowKey(crewDetail, index);
                  const rowExpanded = expandedDispatchKeys.has(rowKey);
                  const deductionItems = readDeductionItems(crewDetail);
                  return (
                    <Box mt={2} key={rowKey}>
                      <Grid item xs={12} md={12}>
                        <Grid container direction="row">
                          <Grid item xs={12} md={12} className={classes.dispatchRowContainer}>
                            <Box pl={1} pr={1} my={1}>
                              <Box className={classes.dispatchRowHeader}>
                                <Box flex={1} minWidth={0}>
                                  <Grid container direction="row">
                                    <Grid item xs={12} md={12}>
                                      <Grid container direction="row" wrap="nowrap">
                                        <Grid item xs={12} md={5} className={classes.crewColVisitDate}>
                                          <Box
                                            display="flex"
                                            flexDirection="row"
                                            flexWrap="nowrap"
                                            alignItems="center"
                                            justifyContent="flex-start"
                                          >
                                            <IconButton
                                              size="small"
                                              aria-expanded={rowExpanded}
                                              aria-label={rowExpanded ? "Collapse dispatch" : "Expand dispatch"}
                                              onClick={() => toggleDispatchRow(rowKey)}
                                              edge="start"
                                              className={classes.dispatchExpandIcon}
                                            >
                                              {rowExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                            </IconButton>
                                            <Box color="text.secondary" mr={1}>
                                              <Typography className={classes.labelText}>Visit Date:</Typography>
                                            </Box>
                                            <Box color="text.primary" ml={0.5}>
                                              <Typography className={classes.valueText}>
                                                {moment(crewDetail.visitDate).format(
                                                  "dddd, MM/DD/YYYY hh:mm A"
                                                )}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4} className={classes.crewColStatus}>
                                          <Box
                                            display="flex"
                                            flexDirection="row"
                                            flexWrap="nowrap"
                                            justifyContent="flex-start"
                                          >
                                            <Box color="text.secondary" mr={1}>
                                              <Typography className={classes.labelText}>Status:</Typography>
                                            </Box>
                                            <Box color="text.primary" ml={0.5}>
                                              <Typography className={classes.valueText}>{crewDetail.status}</Typography>
                                            </Box>
                                          </Box>
                                        </Grid>
                                        <Grid item xs={12} md={3} className={classes.crewColDeductions}>
                                          <Box
                                            display="flex"
                                            flexDirection="row"
                                            flexWrap="nowrap"
                                            justifyContent="flex-start"
                                          >
                                            <Box color="text.secondary" mr={1}>
                                              <Typography className={classes.labelText}>Deductions:</Typography>
                                            </Box>
                                            <Box color="text.primary" ml={0.5}>
                                              <Typography className={classes.valueText}>
                                                {moneyformatter(crewDetail.deductions)}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
                                    <Box className={classes.deductionsSection}>
                                      <Typography component="div" className={classes.deductionsTitle}>
                                        Deductions
                                      </Typography>
                                      {deductionItems.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                          No deduction line items.
                                        </Typography>
                                      ) : (
                                        deductionItems.map((item, di) => (
                                          <Box key={di} className={classes.deductionRow}>
                                            <Grid container spacing={1} direction="row">
                                              <Grid item xs={12} sm={4} md={3}>
                                                <Box display="flex" flexDirection="row" flexWrap="nowrap">
                                                  <Box color="text.secondary" mr={1}>
                                                    <Typography variant="body2" className={classes.labelText}>
                                                      Amount:
                                                    </Typography>
                                                  </Box>
                                                  <Typography variant="body2" className={classes.valueText}>
                                                    {moneyformatter(deductionAmount(item))}
                                                  </Typography>
                                                </Box>
                                              </Grid>
                                              <Grid item xs={12} sm={8} md={9}>
                                                <Box display="flex" flexDirection="row" flexWrap="nowrap">
                                                  <Box color="text.secondary" mr={1}>
                                                    <Typography variant="body2" className={classes.labelText}>
                                                      Reason:
                                                    </Typography>
                                                  </Box>
                                                  <Typography variant="body2" className={classes.valueText}>
                                                    {deductionReason(item)}
                                                  </Typography>
                                                </Box>
                                              </Grid>
                                            </Grid>
                                          </Box>
                                        ))
                                      )}
                                    </Box>
                                  </Collapse>
                                </Box>
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={12}>
                            <Divider sx={{ borderColor: "#ececec" }} />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  );
}
